import * as React from 'react';
import { getShippers, fetchLinkedFFs, fetchOrganisationFFGroups } from 'src/api/masters';
import { FFAndGroup } from 'src/components/common/Types/CommonTypes';
import { fetchStrategicFFs, fetchStrategicTruckers } from 'src/api/inquiry';
import { FormFieldConfig, KeyLabel } from './Types';
import Form from 'antd/lib/form';
import InputNumber from 'antd/lib/input-number';
import Col from 'antd/lib/col';
import message from 'antd/lib/message';
import Spin from 'antd/lib/spin';
import Input from 'antd/lib/input';
import { InquiryModes } from './Constants';
import TextArea from 'antd/lib/input/TextArea';
import { isValidNumber } from './Helper';

const renderSearchLoading = (isSearching: boolean, notFoundText: string) => {
  if (isSearching) {
    return <Spin style={{ margin: '20% 48%' }} />;
  }
  return notFoundText;
};

const renderSelectDropDown = (menu, isLoading: boolean) => {
  if (isLoading) {
    return <Spin style={{ margin: '20px 0 20px 45%' }} />;
  }
  return menu;
};

const fetchShippers = async (queryString) => {
  let shippersArray = [];
  const response = await getShippers({ queryString });
  if (response.isSuccess) {
    shippersArray = response.data.map(shipper => ({
      id: shipper.id,
      name: shipper.name,
    }));
  } else {
    message.error(response.errorMessage);
  }
  return shippersArray;
};

const fetchFFsAndGroups = async (organisationType, mode) => {
  const responses = await Promise.all([
    fetchLinkedFFs({ organisationType, mode }),
    fetchOrganisationFFGroups({}),
  ]);
  let fetchedFFs: FFAndGroup [] = [];
  responses.forEach((response, index) => {
    if (!response.isSuccess) {
      message.error(response.errorMessage);
    }
    const formatResponse = response.data ? response.data.map((item) => {
      return {
        key: item.id,
        label: item.name,
        isGroup: index !== 0,
      };
    }) : [];
    fetchedFFs = [...fetchedFFs, ...formatResponse];
  });
  return fetchedFFs;
};

const fetchStrategicVendorList = async (vendorList, orgList, mode) => {
  const response = mode === InquiryModes.TRAILER ? await fetchStrategicTruckers() : await fetchStrategicFFs();
  let vendors : KeyLabel [] = [];
    if (response.isSuccess) {
      vendors = response.data?.filter(vendor =>
        orgList?.indexOf(vendor.id) === -1 && vendorList?.indexOf(vendor.id) === -1)
        ?.map(item => ({
        key: item.id,
        label: item.name,
      })) || [];
    } else {
      message.error(response.errorMessage);
    }
    return vendors;
};

const renderLine = (className): React.ReactElement => {
  return <div className={className}/>;
};

const renderDot = (styles, className): React.ReactElement => {
  return <div className={className} style={{ ...styles }} />;
};

const renderPortOptionWithFlag = (label, className: { countryName?, countryCode?, countryFlag?, selectedValueStyle? }) : React.ReactElement => {
  return (
    <div className={className.selectedValueStyle}>
      <img src={label?.flagS3Link || label?.flag_s3_link} style={{ maxWidth: '25px' }} className={className.countryFlag} />
      <span className={className.countryCode} style={{ marginLeft: '5px' }}>{label?.code}</span>
      <span className={className.countryName}>{label?.name}</span>
    </div>
  );
};

const renderPortDropdownOptionsWithCountry = (label, classes, enableWetPortTag = false) : React.ReactElement => {
  const isPortGroup = label?.filterType === 'portGroup';
  const countryName = label?.countryName || label?.country_name || label?.name;
  const locationDetail = isPortGroup
    ? 'Port Group'
    : (label?.state ? [label.state, countryName].filter(Boolean).join(', ')  : label?.name);
  return (
    <div className={classes.dropdownDiv} title={label?.code}>
      <div>
        <span className={classes.countryCode}>
          {isPortGroup ? label?.group_name : label?.code }
        </span>
        {label?.type === 'DRY' && <span className={classes.dryPort}>ICD</span>}
        {label?.type === 'WET' && enableWetPortTag && <span className={classes.dryPort}>PORT</span>}
        
        <div style={{ fontSize: '12px', marginRight: '25px' }}>{isPortGroup ? <span>Port Group</span> : locationDetail}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ marginRight: '6px', fontSize: '12px' }}>{label?.countryCode}</span>
        <img src={label?.flagS3Link || label?.flag_s3_link} style={{ maxWidth: '25px' }} />
      </div>
    </div>
  );
};

const getConditionalBullets = (arrayList, displayKey = '') : React.ReactElement => {
  if (!arrayList) return <div></div>;
  return (
    <React.Fragment>
      {arrayList.length > 1 ? <ul style={{ paddingLeft: '20px'}}>
        {arrayList.map(item => (
          <li style={{ marginBottom: '6px' }}>{displayKey ? item?.[displayKey]??'-' : item}</li>
        ))}
      </ul> : displayKey ? arrayList[0]?.[displayKey]??'-' : arrayList[0]}
    </React.Fragment>
  )
}

const renderInputField = (form, key, placeholder, required = false, span = 10, leftPadding = 12): React.ReactElement => {
  return (
    <Col span={span} style={{ paddingLeft: leftPadding }}>
      <Form.Item>
        {form.getFieldDecorator(key, {
          rules: [{ required, message: 'Required' }],
        })(<Input placeholder={placeholder} />)}
      </Form.Item>
    </Col>
  );
};

const renderLabelAndInput = (props) : React.ReactElement => {
  const {
    colSpan,
    label,
    key,
    form,
    classes,
    rules,
    initialValue,
    placeholder,
    disabled,
    labelStyles,
    onChange,
    formStyles,
  } = props;
  return (
    <React.Fragment>
      <Col span={colSpan?.left} className={classes.label} style={{ ...labelStyles }}>
        {label}
      </Col>
      <Col span={colSpan?.right}>
      <Form.Item style={{ ...formStyles }}>
        {form.getFieldDecorator(key, {
          rules,
          initialValue,
        })(
          <Input
            placeholder={placeholder}
            className={classes.inputField}
            disabled={disabled}
            onChange={onChange}
          />
        )}
      </Form.Item>
    </Col>
    </React.Fragment>
  );
};

const renderLabelAndTextArea = (props) : React.ReactElement => {
  const {
    colSpan,
    label,
    key,
    form,
    classes,
    rules,
    initialValue,
    placeholder,
    rows,
    disabled,
    onChange,
    formStyles,
  } = props;
  return (
    <React.Fragment>
      <Col span={colSpan?.left} className={classes.textAreaLabel}>
        {label}
      </Col>
      <Col span={colSpan?.right}>
      <Form.Item style={{ ...formStyles }}>
        {form.getFieldDecorator(key, {
          rules,
          initialValue,
        })(
          <TextArea
            rows={rows || 2}
            placeholder={placeholder}
            className={classes.textAreaField}
            disabled={disabled}
            onChange={onChange}
          />
        )}
      </Form.Item>
    </Col>
    </React.Fragment>
  );
};

const renderLabelAndInputNumber = (props) : React.ReactElement => {
  const {
    colSpan,
    label,
    key,
    form,
    classes,
    rules,
    initialValue,
    placeholder,
    min,
    precision,
    disabled,
    max,
    formStyles,
  } = props;
  return (
    <React.Fragment>
      <Col span={colSpan?.left} className={classes.label}>
        {label}
      </Col>
      <Col span={colSpan?.right}>
      <Form.Item style={{ ...formStyles }}>
        {form.getFieldDecorator(key, {
          rules,
          initialValue,
        })(
          <InputNumber
            placeholder={placeholder}
            className={classes.inputNumberField}
            min={min}
            max={max}
            precision={precision}
            type="number"
            disabled={disabled}
          />
        )}
      </Form.Item>
    </Col>
    </React.Fragment>
  );
};

const renderInputNumber = (props) : React.ReactElement => {
  const {
    key,
    form,
    formItemClasses,
    rules,
    initialValue,
    placeholder,
    min,
    precision,
    disabled,
    max,
    inputClasses,
    formatter,
  } = props;
  return (
    <Form.Item
      className={formItemClasses}
    >
      {form.getFieldDecorator(key, {
        rules,
        initialValue
      })(
        <InputNumber
          disabled={disabled}
          min={min}
          max={max}
          className={inputClasses}
          placeholder={placeholder}
          precision={precision}
          formatter={formatter}
        />
      )}
    </Form.Item>
  );
};

const numberLengthValidator = (rule, value, callback, minMax) => {
  if (isValidNumber(value) && minMax?.max && value > Math.pow(10, minMax?.max)) {
    callback(`Maximum ${minMax?.max} digits allowed`);
  } else if (isValidNumber(value) && minMax?.min && value < Math.pow(10, minMax?.max)) {
    callback(`Minimum ${minMax?.min} digits allowed`);
  } else callback();
}




export {
  renderSearchLoading,
  renderSelectDropDown,
  fetchShippers,
  fetchFFsAndGroups,
  renderLine,
  renderDot,
  fetchStrategicVendorList,
  renderPortOptionWithFlag,
  renderPortDropdownOptionsWithCountry,
  getConditionalBullets,
  renderInputField,
  renderLabelAndInput,
  renderLabelAndTextArea,
  renderLabelAndInputNumber,
  numberLengthValidator,
  renderInputNumber
};
